import React, { useState, useEffect } from 'react';
import moment from 'moment';
import ActionButtons from './ActionButtons';

// Remove HTML tags from a string
const removeHTMLTags = (str: string) => {
    return str.replace(/<\/?[^>]+(>|$)/g, "");
};

interface TableBodyProps<T> {
    data: T[];
    onDelete: (id: string) => void;
    onEdit?: (item: T) => void;
    lang: string;
    onReorder?: (newOrder: T[]) => Promise<void>;
    editable: boolean
}

const TableBody = <T extends { _id: string;[key: string]: any }>({
    data,
    onDelete,
    onEdit,
    lang,
    onReorder,
    editable
}: TableBodyProps<T>) => {
    const [localData, setLocalData] = useState<T[]>(data);
    const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

    // Sync localData when prop data changes
    useEffect(() => {
        setLocalData(data);
    }, [data]);

    // Drag handlers
    const handleDragStart = (index: number) => {
        setDraggedIndex(index);
    };

    const handleDragOver = (index: number, e: React.DragEvent<HTMLTableRowElement>) => {
        e.preventDefault();
        if (draggedIndex === null || draggedIndex === index) return;

        const newOrder = [...localData];
        const draggedItem = newOrder[draggedIndex];
        newOrder.splice(draggedIndex, 1);
        newOrder.splice(index, 0, draggedItem);
        setDraggedIndex(index);
        setLocalData(newOrder);
    };

    const handleDragEnd = async () => {
        setDraggedIndex(null);
        if (onReorder) {
            await onReorder(localData);
        }
    };

    return (
        <tbody>
            {localData.length === 0 ? (
                <tr>
                    <td colSpan={5} className="px-4 py-2 text-center text-gray-500">
                        No items found
                    </td>
                </tr>
            ) : (
                localData.map((item, index) => (
                    <tr
                        key={item._id}
                        draggable={editable}
                        onDragStart={() => handleDragStart(index)}
                        onDragOver={(e) => handleDragOver(index, e)}
                        onDragEnd={handleDragEnd}
                        className={`border-t ${editable ? 'hover:bg-gray-100 cursor-move' : ''}`}
                    >
                            {/* Careers: show Active EN and Active MK columns */}
                            {item.active && typeof item.active === 'object' && 'en' in item.active && 'mk' in item.active ? (
                                <>
                                    {/* Render all other fields except _id, __v, category, active */}
                                    {Object.keys(item)
                                        .filter((key) => !['_id', '__v', 'category', 'active'].includes(key))
                                        .map((key) => {
                                            const k = key as keyof T;
                                            let value = item[k];

                                            if (k === 'createdBy') {
                                                return (
                                                    <td key={key} className="px-4 py-2 truncate max-w-[100px]">
                                                        <span>{value?.name || ''}</span>
                                                    </td>
                                                );
                                            }
                                            if (k === 'updatedBy') {
                                                return (
                                                    <td key={key} className="px-4 py-2 truncate max-w-[100px]">
                                                        <span>{value?.name || '/'}</span>
                                                    </td>
                                                );
                                            }
                                            if (value && typeof value === 'object' && !Array.isArray(value)) {
                                                value = value[lang] ?? value.en ?? value.mk ?? '';
                                            }
                                            if (k === 'createdAt' || k === 'updatedAt') {
                                                return (
                                                    <td key={String(k)} className="px-4 py-2 truncate max-w-[100px]">
                                                        <span>{moment(value as string).format("DD.MM.YYYY")}</span>
                                                    </td>
                                                );
                                            }
                                            if (k === 'image' && typeof value === 'string') {
                                                return (
                                                    <td key={String(k)} className="px-4 py-2 truncate max-w-[100px]">
                                                        {value.trim() ? (
                                                            <img
                                                                src={value}
                                                                alt="Item"
                                                                className="w-16 h-16 object-cover rounded border"
                                                            />
                                                        ) : (
                                                            <span className="text-gray-400 text-sm italic">No image preview</span>
                                                        )}
                                                    </td>
                                                );
                                            }
                                            if (k === 'images' && Array.isArray(value)) {
                                                return (
                                                    <td key={String(k)} className="px-4 py-2 truncate max-w-[100px]">
                                                        <div className="flex space-x-2">
                                                            {value.map((imgUrl: string, idx: number) => (
                                                                <img
                                                                    key={idx}
                                                                    src={imgUrl}
                                                                    alt={`Item ${idx}`}
                                                                    className="w-16 h-16 object-cover rounded border"
                                                                />
                                                            ))}
                                                        </div>
                                                    </td>
                                                );
                                            }
                                            if (typeof value === 'object') {
                                                return (
                                                    <td key={String(k)} className="px-4 py-2 truncate max-w-[100px]">
                                                        <span>{JSON.stringify(value)}</span>
                                                    </td>
                                                );
                                            }
                                            return (
                                                <td key={String(k)} className="px-4 py-2 truncate max-w-[100px]">
                                                    <span>{removeHTMLTags(String(value))}</span>
                                                </td>
                                            );
                                        })}
                                    {/* Active EN column */}
                                    <td className="px-4 py-2">
                                        <span className={`inline-block w-3 h-3 rounded-full ${item.active.en ? 'bg-green-500' : 'bg-red-500'}`} title={item.active.en ? 'Active' : 'Inactive'} />
                                    </td>
                                    {/* Active MK column */}
                                    <td className="px-4 py-2">
                                        <span className={`inline-block w-3 h-3 rounded-full ${item.active.mk ? 'bg-green-500' : 'bg-red-500'}`} title={item.active.mk ? 'Active' : 'Inactive'} />
                                    </td>
                                    {/* Active SQ column */}
                                    <td className="px-4 py-2">
                                        <span className={`inline-block w-3 h-3 rounded-full ${item.active.sq ? 'bg-green-500' : 'bg-red-500'}`} title={item.active.sq ? 'Active' : 'Inactive'} />
                                    </td>
                                </>
                            ) : (
                                // Default rendering for non-career items
                                Object.keys(item)
                                    .filter((key) => !['_id', '__v', 'category'].includes(key))
                                    .map((key) => {
                                        const k = key as keyof T;
                                        let value = item[k];
                                        if (k === 'createdBy') {
                                            return (
                                                <td key={key} className="px-4 py-2 truncate max-w-[100px]">
                                                    <span>{value?.name || ''}</span>
                                                </td>
                                            );
                                        }
                                        if (k === 'updatedBy') {
                                            return (
                                                <td key={key} className="px-4 py-2 truncate max-w-[100px]">
                                                    <span>{value?.name || '/'}</span>
                                                </td>
                                            );
                                        }
                                        if (value && typeof value === 'object' && !Array.isArray(value)) {
                                            value = value[lang] ?? value.en ?? value.mk ?? '';
                                        }
                                        if (k === 'active') {
                                            return (
                                                <td key={String(k)} className="px-4 py-2">
                                                    <span className={`inline-block w-3 h-3 rounded-full ${value ? 'bg-green-500' : 'bg-red-500'}`} title={value ? 'Active' : 'Inactive'} />
                                                </td>
                                            );
                                        }
                                        if (k === 'createdAt' || k === 'updatedAt') {
                                            return (
                                                <td key={String(k)} className="px-4 py-2 truncate max-w-[100px]">
                                                    <span>{moment(value as string).format("DD.MM.YYYY")}</span>
                                                </td>
                                            );
                                        }
                                        if (k === 'image' && typeof value === 'string') {
                                            return (
                                                <td key={String(k)} className="px-4 py-2 truncate max-w-[100px]">
                                                    {value.trim() ? (
                                                        <img
                                                            src={value}
                                                            alt="Item"
                                                            className="w-16 h-16 object-cover rounded border"
                                                        />
                                                    ) : (
                                                        <span className="text-gray-400 text-sm italic">No image preview</span>
                                                    )}
                                                </td>
                                            );
                                        }
                                        if (k === 'images' && Array.isArray(value)) {
                                            return (
                                                <td key={String(k)} className="px-4 py-2 truncate max-w-[100px]">
                                                    <div className="flex space-x-2">
                                                        {value.map((imgUrl: string, idx: number) => (
                                                            <img
                                                                key={idx}
                                                                src={imgUrl}
                                                                alt={`Item ${idx}`}
                                                                className="w-16 h-16 object-cover rounded border"
                                                            />
                                                        ))}
                                                    </div>
                                                </td>
                                            );
                                        }
                                        if (typeof value === 'object') {
                                            return (
                                                <td key={String(k)} className="px-4 py-2 truncate max-w-[100px]">
                                                    <span>{JSON.stringify(value)}</span>
                                                </td>
                                            );
                                        }
                                        return (
                                            <td key={String(k)} className="px-4 py-2 truncate max-w-[100px]">
                                                <span>{removeHTMLTags(String(value))}</span>
                                            </td>
                                        );
                                    })
                            )}
                        <ActionButtons
                            onEdit={onEdit ? () => onEdit(item) : undefined}
                            onDelete={() => onDelete(item._id)}
                        />
                    </tr>
                ))
            )}
        </tbody>
    );
};

export default TableBody;
